import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, getDocs, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../utils/init-firebase';
import { Upload, FileText, Trash } from 'lucide-react';
import '../index.css';

// Constants for file validation
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = ['application/pdf'];

const Add = () => {
  const [fields, setFields] = useState({});
  const [allColumns, setAllColumns] = useState([]);
  const [errors, setErrors] = useState({});
  const [suggestionsData, setSuggestionsData] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState({});
  const [nCopies, setNCopies] = useState(1); // Keep as number
  const [nCopiesInput, setNCopiesInput] = useState('1'); // New state for text input value
  const [pastedSerialNumbers, setPastedSerialNumbers] = useState(Array(1).fill(''));
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPDFs, setSelectedPDFs] = useState([]);
  const [uploadingPDFs, setUploadingPDFs] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  const inputRefs = useRef({});
  const pastedListRefs = useRef({});
  const fileInputRef = useRef(null);

  // Sync nCopies state with pastedSerialNumbers array length
  useEffect(() => {
    setPastedSerialNumbers(prev => {
      const newArray = Array(nCopies).fill('');
      for (let i = 0; i < Math.min(prev.length, nCopies); i++) {
        newArray[i] = prev[i];
      }
      return newArray;
    });
  }, [nCopies]);

  // Fetch existing fields on component mount
  useEffect(() => {
    const fetchExistingFields = async () => {
      setIsLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'cloudtree-database'));
        const docs = snapshot.docs.map(doc => doc.data());

        if (docs.length > 0) {
          const keys = new Set();
          docs.forEach(doc => Object.keys(doc).forEach(key => keys.add(key)));

          let columns = Array.from(keys).filter(
            key => !key.toLowerCase().includes('pdf') && key.toLowerCase() !== 'id'
          );

          columns = columns.sort((a, b) => {
            const nameA = String(a).toLowerCase();
            const nameB = String(b).toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });

          setAllColumns(columns);

          const newSuggestions = {};
          columns.forEach(col => {
            const valuesSet = new Set();
            docs.forEach(doc => {
              if (doc[col]) {
                valuesSet.add(doc[col]);
              }
            });
            newSuggestions[col] = Array.from(valuesSet).sort((a, b) => {
              const valA = String(a).toLowerCase();
              const valB = String(b).toLowerCase();
              if (valA < valB) return -1;
              if (valA > valB) return 1;
              return 0;
            });
          });
          setSuggestionsData(newSuggestions);
        }
      } catch (error) {
        console.error("Error fetching existing fields:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExistingFields();
  }, []);

  const handleChange = (e, field) => {
    const trimmedValue = e.target.value.trimStart();
    setFields(prev => ({
      ...prev,
      [field]: trimmedValue,
    }));
    setErrors(prev => ({ ...prev, [field]: false }));
    setShowSuggestions(prev => ({ ...prev, [field]: true }));
    setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));

    // If 'Serial Number' field is manually changed, clear pasted serials
    if (field.toLowerCase() === 'serial number') {
      setPastedSerialNumbers(Array(nCopies).fill(''));
    }
  };

  const filteredSuggestions = useCallback((field) => {
    if (!fields[field]) {
      return suggestionsData[field] || [];
    }
    const inputVal = fields[field].toLowerCase();
    const filtered = (suggestionsData[field] || []).filter(s =>
      String(s).toLowerCase().includes(inputVal)
    );
    return filtered.sort((a, b) => {
      const valA = String(a).toLowerCase();
      const valB = String(b).toLowerCase();
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    }).slice(0, 5);
  }, [fields, suggestionsData]);

  const handleSuggestionClick = useCallback((field, suggestion) => {
    setFields(prev => ({
      ...prev,
      [field]: suggestion,
    }));
    setShowSuggestions(prev => ({ ...prev, [field]: false }));
    setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));

    // If 'Serial Number' field is populated via suggestion, clear pasted serials
    if (field.toLowerCase() === 'serial number') {
      setPastedSerialNumbers(Array(nCopies).fill(''));
    }

    inputRefs.current[field]?.focus();
  }, [nCopies]);

  const handleBlur = (field) => {
    setTimeout(() => {
      setShowSuggestions(prev => ({ ...prev, [field]: false }));
      setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));
    }, 150);
  };

  // --- Keyboard Navigation Handlers ---

  const handleKeyDown = (e, field) => {
    const suggestions = filteredSuggestions(field);
    const currentIndex = activeSuggestionIndex[field] ?? -1;

    // Suggestions navigation (Arrow keys, Enter)
    if (showSuggestions[field] && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex + 1 >= suggestions.length ? 0 : currentIndex + 1;
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: nextIndex }));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex - 1 < 0 ? suggestions.length - 1 : currentIndex - 1;
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: prevIndex }));
      } else if (e.key === 'Enter') {
        if (currentIndex >= 0 && currentIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionClick(field, suggestions[currentIndex]);
        }
      }
    }

    // Tab navigation (Tab, Shift+Tab)
    if (e.key === 'Tab') {
      e.preventDefault();

      if (e.shiftKey) { // Shift + Tab (go backwards)
        setShowSuggestions(prev => ({ ...prev, [field]: false }));
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));

        const currentIdx = allColumns.indexOf(field);
        if (currentIdx > 0) {
          inputRefs.current[allColumns[currentIdx - 1]]?.focus();
        } else {
          // If it's the first standard column, go back to nCopies
          inputRefs.current.nCopiesInput?.focus();
        }
      } else { // Tab (go forwards)
        if (showSuggestions[field] && (currentIndex >= 0 && currentIndex < suggestions.length)) {
          handleSuggestionClick(field, suggestions[currentIndex]);
        }
        setShowSuggestions(prev => ({ ...prev, [field]: false }));
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));

        const currentIdx = allColumns.indexOf(field);
        const serialNumberCol = allColumns.find(col => col.toLowerCase() === 'serial number');
        const hasPastedSerials = serialNumberCol && pastedSerialNumbers.length > 0;

        if (currentIdx < allColumns.length - 1) {
          inputRefs.current[allColumns[currentIdx + 1]]?.focus();
        } else {
          // If it's the last standard column, go to first editable serial number (if exists) or submit
          if (hasPastedSerials && pastedListRefs.current[0]) {
            pastedListRefs.current[0]?.focus();
          } else {
            document.querySelector('.submit-btn')?.focus();
          }
        }
      }
    }
  };

  const handleNCopiesInputChange = (e) => {
    const value = e.target.value.trim();
    setNCopiesInput(value); // Update the text input state

    // Validate and update the numeric nCopies state
    if (value === '' || value === '0') {
      setNCopies(1);
    } else {
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setNCopies(parsedValue);
      } else if (isNaN(parsedValue) && value !== '-') { // Allow typing a '-' temporarily for negative numbers before it's corrected
        // If not a valid number, default to 1, but keep the invalid text in input state
        setNCopies(1);
      }
    }
  };

  const handleNCopiesKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      if (e.shiftKey) { // Shift + Tab from nCopies (go backwards)
        // Go to submit button (circular focus)
        document.querySelector('.submit-btn')?.focus();
      } else { // Tab from nCopies (go forwards)
        // Go to the first standard column field, if available
        if (allColumns.length > 0) {
          inputRefs.current[allColumns[0]]?.focus();
        } else if (pastedListRefs.current[0]) {
          // If no standard columns, go to first editable serial number
          pastedListRefs.current[0]?.focus();
        } else {
          // Fallback, go to submit button
          document.querySelector('.submit-btn')?.focus();
        }
      }
    }
  };

  const handlePaste = (e, field) => {
    if (field.toLowerCase() === 'serial number') {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const serials = pastedText.split(/\r?\n/).filter(line => line.trim() !== '');

      // Set nCopies based on pasted serials if more than current nCopies
      if (serials.length > nCopies) {
        setNCopies(serials.length);
        setNCopiesInput(String(serials.length));
      } else if (serials.length === 0 && nCopies === 0) {
        setNCopies(1); // Ensure nCopies doesn't become 0 if everything is deleted
        setNCopiesInput('1');
      }

      setPastedSerialNumbers(prev => {
        const newArray = Array(Math.max(nCopies, serials.length)).fill(''); // Ensure array is at least nCopies long, or pasted length
        for (let i = 0; i < Math.min(newArray.length, serials.length); i++) {
          newArray[i] = serials[i].trim();
        }
        return newArray;
      });

      // Update the main 'Serial Number' field to the first pasted value if available
      setFields(prev => ({
        ...prev,
        [field]: serials.length > 0 ? serials[0].trim() : '',
      }));
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handlePastedSerialEdit = (index, newValue) => {
    const updatedSerialNumbers = [...pastedSerialNumbers];
    updatedSerialNumbers[index] = newValue.trim();
    setPastedSerialNumbers(updatedSerialNumbers);

    // If the first pasted serial number is edited, update the main 'Serial Number' field too
    if (index === 0) {
      setFields(prev => ({
        ...prev,
        'Serial Number': newValue.trim(),
      }));
    }
  };

  const handlePastedSerialKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur(); // Blur current field to trigger onBlur update

      if (index < nCopies - 1) { // If not the last editable SN field
        pastedListRefs.current[index + 1]?.focus();
      } else {
        document.querySelector('.submit-btn')?.focus();
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();

      if (e.shiftKey) { // Shift + Tab (go backwards)
        if (index > 0) {
          pastedListRefs.current[index - 1]?.focus();
        } else {
          // If it's the first pasted item, go to last standard column or nCopies
          if (allColumns.length > 0) {
            inputRefs.current[allColumns[allColumns.length - 1]]?.focus();
          } else {
            inputRefs.current.nCopiesInput?.focus();
          }
        }
      } else { // Tab (go forwards)
        if (index < nCopies - 1) {
          pastedListRefs.current[index + 1]?.focus();
        } else {
          // If it's the last item, go to the submit button
          document.querySelector('.submit-btn')?.focus();
        }
      }
    }
  };

  const validateFiles = (files) => {
    const errors = [];
    let totalSize = 0;

    // Check each file
    for (const file of files) {
      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name} is not a PDF file`);
        continue;
      }

      // Check individual file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds the 10MB size limit`);
        continue;
      }

      totalSize += file.size;
    }

    // Check total size
    if (totalSize > MAX_TOTAL_SIZE) {
      errors.push(`Total size of all files (${(totalSize / (1024 * 1024)).toFixed(2)}MB) exceeds the 50MB limit`);
    }

    return { errors, totalSize };
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const { errors, totalSize } = validateFiles(files);
    
    if (errors.length > 0) {
      setUploadError(errors.join('\n'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Add new files to existing ones
    setSelectedPDFs(prev => [...prev, ...files]);
    setUploadError('');
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedPDF = (index, event) => {
    // Prevent validation from triggering
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setSelectedPDFs(prev => prev.filter((_, i) => i !== index));
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadPDFs = async (docId) => {
    if (selectedPDFs.length === 0) return [];

    setUploadingPDFs(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const uploadPromises = selectedPDFs.map(async (file, index) => {
        try {
          // Create a unique filename with timestamp and original name
          const timestamp = new Date().getTime();
          const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const filename = `${docId}_${timestamp}_${safeFileName}`;
          const storageRef = ref(storage, `pdfs/${filename}`);

          // Upload file with metadata
          const metadata = {
            contentType: 'application/pdf',
            customMetadata: {
              originalName: file.name,
              uploadedBy: auth.currentUser?.email || 'unknown',
              uploadedAt: new Date().toISOString()
            }
          };

          await uploadBytes(storageRef, file, metadata);
          const downloadURL = await getDownloadURL(storageRef);

          // Update progress
          setUploadProgress(((index + 1) / selectedPDFs.length) * 100);

          return {
            name: file.name,
            url: downloadURL,
            path: storageRef.fullPath,
            size: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: auth.currentUser?.email || 'unknown'
          };
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        }
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      return uploadedFiles;

    } catch (error) {
      console.error('Error uploading PDFs:', error);
      setUploadError(error.message || 'Failed to upload PDFs. Please try again.');
      return [];
    } finally {
      setUploadingPDFs(false);
      setUploadProgress(0);
      setSelectedPDFs([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // --- Validation and Submission ---

  const validate = () => {
    const newErrors = {};
    const cleanedFields = {};

    // Validate standard fields (excluding Serial Number, as it's handled separately)
    allColumns.forEach(field => {
      if (field.toLowerCase() !== 'serial number') {
        const value = (fields[field] || '').trim();
        cleanedFields[field] = value;
        if (value === '') {
          newErrors[field] = true;
        }
      } else {
        // For Serial Number, just ensure it's in cleanedFields for submission structure
        cleanedFields[field] = fields[field] || '';
      }
    });

    setFields(cleanedFields);
    setErrors(newErrors);

    // Validate nCopies
    if (nCopies < 1 || isNaN(nCopies)) {
      alert('Number of copies (n) must be a positive integer.');
      return false;
    }

    // Validate the dynamically generated serial number fields
    const serialNumberColumnExists = allColumns.some(col => col.toLowerCase() === 'serial number');
    if (serialNumberColumnExists) {
      const hasEmptySerial = pastedSerialNumbers.slice(0, nCopies).some(s => s.trim() === '');
      if (hasEmptySerial) {
        alert('All serial number fields must be filled.');
        return false;
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert('You must be logged in to add a record.');
        return;
      }

      const serialNumberColumnName = allColumns.find(col => col.toLowerCase() === 'serial number');

      for (let i = 0; i < nCopies; i++) {
        // First add the document to get its ID
        const docRef = await addDoc(collection(db, 'cloudtree-database'), {
          ...fields,
          [serialNumberColumnName]: pastedSerialNumbers[i].trim()
        });

        // Upload PDFs if any are selected
        if (selectedPDFs.length > 0) {
          const uploadedPDFs = await uploadPDFs(docRef.id);
          if (uploadedPDFs.length > 0) {
            // Update the document with PDF URLs
            await updateDoc(docRef, { PDFS: uploadedPDFs });
          }
        }
      }

      alert(`${nCopies} document(s) added successfully!`);

      // Reset form after successful submission
      setFields({});
      setErrors({});
      setNCopies(1);
      setNCopiesInput('1');
      setPastedSerialNumbers(Array(1).fill(''));
      setSelectedPDFs([]);
      setUploadError('');

    } catch (error) {
      console.error('Error adding document:', error);
      alert('Failed to add document. Check console for details.');
    }
  };

  const serialNumberCol = allColumns.find(col => col.toLowerCase() === 'serial number');

  // Add PDF upload section to the form
  const renderPDFUploadSection = () => (
    <div className="form-row full-width-row">
      <label>Attach PDFs</label>
      <div className="pdf-upload-container">
        <label className="pdf-upload-label">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf"
            multiple
            className="pdf-upload-input"
            disabled={uploadingPDFs}
            onClick={(e) => e.stopPropagation()} // Prevent event bubbling
          />
          <Upload size={24} className="pdf-upload-icon" />
          <div className="pdf-upload-text">
            <strong>Click to upload</strong>
            <div className="pdf-upload-limits">
              (Max file size: 10MB, Total size: 50MB)
            </div>
          </div>
        </label>
      </div>

      {uploadingPDFs && (
        <div className="pdf-upload-progress">
          <div
            className="pdf-upload-progress-bar"
            style={{ width: `${uploadProgress}%` }}
          />
          <span className="pdf-upload-progress-text">
            Uploading... {Math.round(uploadProgress)}%
          </span>
        </div>
      )}

      {uploadError && (
        <div className="pdf-upload-error" role="alert">
          {uploadError.split('\n').map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}

      {selectedPDFs.length > 0 && (
        <div className="pdf-list">
          {selectedPDFs.map((file, index) => (
            <div key={index} className="pdf-item">
              <div className="pdf-item-info">
                <FileText size={20} className="pdf-item-icon" />
                <span className="pdf-item-name">{file.name}</span>
                <span className="pdf-item-size">
                  ({(file.size / (1024 * 1024)).toFixed(2)}MB)
                </span>
              </div>
              <div className="pdf-item-actions">
                <button
                  type="button" // Add type="button" to prevent form submission
                  className="pdf-item-button delete"
                  onClick={(e) => removeSelectedPDF(index, e)}
                  title="Remove PDF"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="secret-pages">
      <div className="dashboard-wrapper">
        <div className="dashboard-container">
          <h1 className="dashboard-header">Add New Entries</h1>
          {isLoading ? (
            <p className="loading-message">Loading fields from database...</p>
          ) : (
            <form onSubmit={handleAdd} className="add-form">
              <div className="form-grid">
                {/* Number of Copies - FIRST FIELD, always full-width */}
                <div className="form-row full-width-row">
                  <label htmlFor="nCopiesInput">Number of Copies (n)</label>
                  <input
                    id="nCopiesInput" // Changed ID
                    type="text" // Changed to text
                    value={nCopiesInput} // Binds to new state
                    onChange={handleNCopiesInputChange}
                    placeholder="Enter number of copies"
                    ref={el => (inputRefs.current.nCopiesInput = el)} // Ref to new state
                    onKeyDown={handleNCopiesKeyDown}
                    className="n-copies-input"
                    pattern="[0-9]*" // Suggests numeric keyboard on mobile
                    inputMode="numeric" // Suggests numeric keyboard on mobile
                  />
                </div>

                {/* All other dynamic fields */}
                {allColumns.map(field => {
                  const isSerialNumberField = field.toLowerCase() === 'serial number';
                  return (
                    <div className="form-row" key={field}>
                      <label htmlFor={field}>{field}</label>
                      {/* Main Serial Number input for paste */}
                      {isSerialNumberField ? (
                        <input
                          id={field}
                          type="text"
                          value={fields[field] || ''}
                          onChange={(e) => handleChange(e, field)} // Can now be edited manually
                          onPaste={(e) => handlePaste(e, field)}
                          onBlur={() => handleBlur(field)} // Add blur for suggestions if needed
                          onKeyDown={(e) => handleKeyDown(e, field)} // Keep keyboard navigation
                          placeholder="Paste serial numbers here or type first one"
                          className={`serial-number-main-input ${errors[field] ? 'input-error' : ''}`}
                          autoComplete="off"
                          ref={el => (inputRefs.current[field] = el)}
                          spellCheck={false}
                        />
                      ) : (
                        // Regular input field
                        <input
                          id={field}
                          type="text"
                          value={fields[field] || ''}
                          onChange={(e) => handleChange(e, field)}
                          onBlur={() => handleBlur(field)}
                          onKeyDown={(e) => handleKeyDown(e, field)}
                          placeholder={`Enter ${field}`}
                          className={errors[field] ? 'input-error' : ''}
                          autoComplete="off"
                          ref={el => (inputRefs.current[field] = el)}
                          spellCheck={false}
                        />
                      )}
                      {showSuggestions[field] && filteredSuggestions(field).length > 0 && (
                        <ul className="suggestions-list">
                          {filteredSuggestions(field).map((sugg, idx) => (
                            <li
                              key={idx}
                              onMouseDown={() => handleSuggestionClick(field, sugg)}
                              className={idx === activeSuggestionIndex[field] ? 'active-suggestion' : ''}
                            >
                              {sugg}
                            </li>
                          ))}
                        </ul>
                      )}
                      {errors[field] && !isSerialNumberField && <p className="error-message">This field is required.</p>}
                    </div>
                  );
                })}

                {/* Dynamically generated editable serial number fields - always visible if SN column exists */}
                {serialNumberCol && (
                  <div className="form-row full-width-row pasted-serials-container">
                    <p className="pasted-serials-info">
                      {nCopies} serial number(s) to be added. Edit or fill below:
                    </p>
                    <ul className="pasted-serials-list">
                      {pastedSerialNumbers.slice(0, nCopies).map((sNum, idx) => ( // Only render up to nCopies
                        <li key={idx} className="pasted-serial-item">
                          <div
                            contentEditable
                            suppressContentEditableWarning={true}
                            onBlur={(e) => handlePastedSerialEdit(idx, e.target.innerText)}
                            onKeyDown={(e) => handlePastedSerialKeyDown(e, idx)}
                            ref={el => (pastedListRefs.current[idx] = el)}
                            className={`editable-serial-num ${sNum.trim() === '' ? 'empty-serial' : ''}`}
                            tabIndex="0"
                            placeholder={`Serial Number ${idx + 1}`}
                          >
                            {sNum}
                          </div>
                        </li>
                      ))}
                    </ul>
                     {/* Error message for pasted serial numbers if any are empty */}
                    {errors[serialNumberCol] && pastedSerialNumbers.slice(0, nCopies).some(s => s.trim() === '') && (
                        <p className="error-message">All serial numbers must be filled.</p>
                    )}
                  </div>
                )}

                {renderPDFUploadSection()}
              </div> {/* End of form-grid */}

              <button type="submit" className="submit-btn" disabled={uploadingPDFs}>
                {uploadingPDFs ? 'Uploading...' : 'Add Entry'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Add;